import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const STATUS_ICONS = { queued: '🟡', building: '🔵', idle: '⚪', success: '🟢', failed: '🔴' };

const BuildQueuePanel = () => {
  const { data: builds, loading, error, retry } = useAsyncData('/api/builds');
  const { isStale, lastUpdated } = useStaleWarning(builds);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <section className="panel build-panel" aria-label="Build Queue" aria-busy="true">
          <h2>Build Queue</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </section>
      </ErrorBoundary>
    );
  }
  if (error) {
    return (
      <ErrorBoundary>
        <section className="panel build-panel" aria-label="Build Queue" aria-live="assertive">
          <h2>Build Queue</h2>
          <p className="error-msg">Failed to load build queue.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </section>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <section className="panel build-panel" aria-label="Build Queue" aria-busy="false">
        <div className="panel-header">
          <h2>Build Queue</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh builds">
              ↻
            </button>
          </div>
        </div>
        <ul className="build-list" aria-label="Build items">
          {(builds || []).length === 0 && <li className="empty">No build items</li>}
          {(builds || []).map((build) => {
            const icon = STATUS_ICONS[build.status] || '⚪';
            return (
              <li className="build-item" key={build.name}>
                <strong>{icon} {build.name}</strong>
                <span className="build-status" aria-label={`Status: ${build.status}`}>{build.status}</span>
                <p className="build-next">{build.next}</p>
              </li>
            );
          })}
        </ul>
      </section>
    </ErrorBoundary>
  );
};

export default BuildQueuePanel;
